import type { Editor } from "@tiptap/core";
import { Node } from "@tiptap/core";
import { ReactNodeViewRenderer, mergeAttributes } from "@tiptap/react";

import { Renderer } from "./Renderer";

export interface PaywallAttrs {
  button_text: string;
  description: string;
  paywall_id: number | null;
  title: string;
}

const hasPaywallNode = (editor: Editor, name: string): boolean => {
  let isFound = false;
  editor.state.doc.descendants((node: { type: { name: string } }) => {
    if (node.type.name === name) isFound = true;
  });
  return isFound;
};

declare module "@tiptap/core" {
  interface Storage {
    paywall?: { isPaywallPresent: boolean };
  }

  interface Commands<ReturnType> {
    paywall: {
      /**
       * Insert a new paywall node at the given range, or replace the
       * attributes of the paywall node under the range when `isEdit` is
       * true. A trailing hard break is only added on insert, so editing
       * an existing node doesn't duplicate the break after it.
       */
      setPaywall: (params: {
        attrs: PaywallAttrs;
        isEdit: boolean;
        range: { from: number; to: number };
      }) => ReturnType;
    };
  }
}

export const PaywallExtension = Node.create({
  name: "paywall",
  group: "block",
  atom: true,
  draggable: false,
  addStorage: () => ({
    isPaywallPresent: false,
  }),
  addAttributes: () => ({
    paywall_id: {
      default: null,
    },
    title: {
      default: null,
    },
    description: {
      default: null,
    },
    button_text: {
      default: null,
    },
  }),
  parseHTML: () => [{ tag: "paywall-react" }],
  renderHTML: ({ HTMLAttributes }) => [
    "paywall-react",
    mergeAttributes(HTMLAttributes),
  ],
  addNodeView: () => ReactNodeViewRenderer(Renderer),
  onCreate() {
    this.storage.isPaywallPresent = hasPaywallNode(this.editor, this.name);
  },
  onUpdate() {
    this.storage.isPaywallPresent = hasPaywallNode(this.editor, this.name);
  },
  addCommands() {
    return {
      setPaywall:
        ({ range, attrs, isEdit }) =>
        ({ chain }) =>
          chain()
            .focus()
            .insertContentAt(
              range,
              [
                { type: this.name, attrs },
                !isEdit && { type: "hardBreak" },
              ].filter(Boolean),
            )
            .run(),
    };
  },
});
